import type { FormFieldConfig } from "./types";
import he from "he";
import { attributes } from "./attributes";

declare global {
  interface Window {
    formconfig: string;
    formClassConfig: FormClassConfig;
    formBuilder: FormBuilder;
    formSender: FormSender;
  }
}

interface FormSender {
  url: string;
  isRedirect: boolean;
  submitUrl: string;
}

interface FormClassConfig {
  formClass: string;
  formContainer: string;
  formFieldWrapper: string;
  formFieldLabel: string;
  formFieldInput: string;
  formFieldSelect: string;
  formFieldRange: string;
  formFieldTwoInputs: string;
  formFieldSubmit: string;
}

export class FormBuilder {
  private formRenderer: FormRenderer | null = null;
  private container: HTMLElement | null = null;
  private formClassConfig: FormClassConfig | null = null;
  private formSender: FormSender | null = null;

  async initialize() {
    this.loadConfig();
    this.render();
    return this;
  }

  private loadConfig() {
    console.log(window.formconfig);
    try {
      const decodedConfig = he.decode(window.formconfig);
      const fields = JSON.parse(decodedConfig);
      console.log("Parsed fields:", fields);

      if (!fields) {
        console.warn("No form configuration found");
        return;
      }
      this.formClassConfig = window.formClassConfig;

      if (!this.formClassConfig) {
        this.formClassConfig = {
          formClass: "event-register_form",
          formContainer: "form-container",
          formFieldWrapper: "layout-vertical-xxsmall",
          formFieldLabel: "label-xsmall",
          formFieldInput: "form_input",
          formFieldSelect: "form_input",
          formFieldRange: "form_input",
          formFieldTwoInputs: "form-field-two-inputs",
          formFieldSubmit: "button is-small w-button",
        };
      }
      this.formSender = window.formSender;
      if (!this.formSender) {
        console.error("No form sender found");
        this.formSender = {
          url: "",
          isRedirect: false,
          submitUrl: "",
        };
      } else {
        console.log("Form sender added:", this.formSender);
      }
      this.formRenderer = new FormRenderer(
        fields.fields || [],
        this.formClassConfig,
        this.formSender
      );
    } catch (error) {
      console.error("Error parsing form config:", error);
      console.log("Raw config:", window.formconfig);
      console.log("Form class config:", window.formClassConfig);
      console.log("Form sender:", window.formSender);
      this.formRenderer = new FormRenderer(
        [],
        this.formClassConfig,
        this.formSender
      );
    }
  }

  private render() {
    // Find or create container
    //console.log(document.querySelector("[data-form-container]"));
    const containers = document.querySelectorAll("[data-form-container]");
    if (containers.length === 0) {
      console.error("No container element found");
      return;
    }
    this.container = containers[0] as HTMLElement;
    if (!this.container) {
      console.error("No container element found");
      return;
    }

    const form = this.formRenderer?.build();
    if (!form) {
      console.error("No form renderer found");
      return;
    }
    this.container.innerHTML = "";
    this.container.appendChild(form);

    //hide states
    this.formRenderer?.hideStates(form);
  }

  async reload() {
    this.loadConfig();
    this.render();
  }
}

class FormRenderer {
  constructor(
    private fields: FormFieldConfig[],
    private formClassConfig: FormClassConfig | null,
    private formSender: FormSender | null
  ) {
    this.addStyles();
    console.log("Form renderer constructor", this.formSender);
  }

  private addStyles() {
    const styleId = "form-payments-styles";
    if (document.getElementById(styleId)) return;

    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      .form-field-two-inputs {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 1rem;
        width: 100%;
      }
      
      .input-side {
        flex: 1;
      }
      
      .input-side.left {
        margin-right: 0.5rem;
      }
      
      .input-side.right {
        margin-left: 0.5rem;
      }
    `;

    document.head.appendChild(style);
  }

  private transformFields(fields: FormFieldConfig[]): FormFieldConfig[] {
    // Get top level fields
    const topLevelFields = fields.filter((f) => !f.parentId);

    // Transform each field
    return topLevelFields.map((field) => {
      if (field.type === "two-inputs") {
        // Find child fields
        const leftField = fields.find(
          (f) => f.parentId === field.id && f.position === "left"
        );
        const rightField = fields.find(
          (f) => f.parentId === field.id && f.position === "right"
        );

        return {
          ...field,
          fields: {
            left: leftField,
            right: rightField,
          },
        };
      }
      return field;
    });
  }

  build(): HTMLFormElement {
    const form = document.createElement("form");
    form.className = "form-payments-form";

    // Transform fields before rendering
    const transformedFields = this.transformFields(this.fields);
    transformedFields.forEach((field) => {
      const fieldElement = this.createField(field);
      if (fieldElement) form.appendChild(fieldElement);
    });

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      console.log("submit");
      await this.handleSubmit(e);
    });

    return form;
  }

  private createField(field: FormFieldConfig): HTMLElement | null {
    const wrapper = document.createElement("div");
    wrapper.className = "form-field-wrapper";
    if (this.formClassConfig?.formFieldWrapper) {
      wrapper.className = this.formClassConfig.formFieldWrapper;
    }

    switch (field.type) {
      case "text":
      case "email":
      case "phone":
        return this.createInputField(field, wrapper);
      case "select":
        return this.createSelectField(field, wrapper);
      case "range":
        return this.createRangeField(field, wrapper);
      case "two-inputs":
        return this.createTwoInputsField(field, wrapper);
      case "submit":
        return this.createSubmitButton(field, wrapper);
      default:
        return null;
    }
  }

  private createInputField(
    field: FormFieldConfig,
    wrapper: HTMLElement
  ): HTMLElement {
    const label = document.createElement("label");
    label.textContent = field.label || "Untitled Field";
    if (field.required) label.classList.add("required");
    if (this.formClassConfig?.formFieldLabel) {
      label.classList.add(this.formClassConfig.formFieldLabel);
    }

    const input = document.createElement("input");
    input.type = field.type;
    input.placeholder = field.placeholder || "";
    input.required = field.required || false;
    input.name = field.id;
    input.id = field.id;
    if (this.formClassConfig?.formFieldInput) {
      input.classList.add(this.formClassConfig.formFieldInput);
    }

    // Add phone number validation pattern
    if (field.type === "phone") {
      input.pattern =
        "^\\+?1?[-. ]?\\(?([0-9]{3})\\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$";
      input.inputMode = "tel";
      input.title =
        "Please enter a valid phone number (e.g., 123-456-7890 or (123) 456-7890)";

      // Add input event listener for real-time validation
      input.addEventListener("input", (e) => {
        const target = e.target as HTMLInputElement;
        // Remove any non-digit characters for validation
        const digits = target.value.replace(/\D/g, "");

        if (digits.length < 10 || digits.length > 11) {
          target.setCustomValidity(
            "Please enter a valid 10-digit phone number"
          );
        } else {
          target.setCustomValidity("");
        }
      });
    }

    wrapper.appendChild(label);
    wrapper.appendChild(input);
    return wrapper;
  }

  private createSelectField(
    field: FormFieldConfig,
    wrapper: HTMLElement
  ): HTMLElement {
    const label = document.createElement("label");
    label.textContent = field.label || "Select Option";
    if (field.required) label.classList.add("required");
    if (this.formClassConfig?.formFieldLabel) {
      label.classList.add(this.formClassConfig.formFieldLabel);
    }
    const select = document.createElement("select");
    if (this.formClassConfig?.formFieldSelect) {
      select.classList.add(this.formClassConfig.formFieldSelect);
    }
    select.required = field.required || false;
    select.name = field.id;
    select.id = field.id;

    if (field.placeholder) {
      const placeholder = document.createElement("option");
      placeholder.value = "";
      placeholder.textContent = field.placeholder;
      placeholder.disabled = true;
      placeholder.selected = true;
      select.appendChild(placeholder);
    }

    field.selectOptions?.forEach((option) => {
      const optionEl = document.createElement("option");
      optionEl.value = option;
      optionEl.textContent = option;
      select.appendChild(optionEl);
    });

    wrapper.appendChild(label);
    wrapper.appendChild(select);
    return wrapper;
  }

  private createRangeField(
    field: FormFieldConfig,
    wrapper: HTMLElement
  ): HTMLElement {
    const label = document.createElement("label");
    label.textContent = field.label || "Range";
    if (field.required) label.classList.add("required");
    if (this.formClassConfig?.formFieldLabel) {
      label.classList.add(this.formClassConfig.formFieldLabel);
    }
    const rangeContainer = document.createElement("div");
    rangeContainer.className = "range-container";

    const input = document.createElement("input");
    input.type = "range";
    input.name = field.id;
    input.id = field.id;
    input.min = String(field.rangeMin || 0);
    input.max = String(field.rangeMax || 100);
    input.step = String(field.rangeStep || 1);
    input.required = field.required || false;

    const valueDisplay = document.createElement("output");
    valueDisplay.setAttribute("for", field.id);
    valueDisplay.textContent = input.value;

    input.addEventListener("input", () => {
      valueDisplay.textContent = input.value;
    });

    rangeContainer.appendChild(input);
    rangeContainer.appendChild(valueDisplay);

    wrapper.appendChild(label);
    wrapper.appendChild(rangeContainer);
    return wrapper;
  }

  private createTwoInputsField(
    field: FormFieldConfig,
    wrapper: HTMLElement
  ): HTMLElement {
    if (this.formClassConfig?.formFieldTwoInputs) {
      wrapper.classList.add(this.formClassConfig.formFieldTwoInputs);
    }

    if (field.fields?.left) {
      const leftWrapper = document.createElement("div");
      leftWrapper.className = "input-side left";
      const leftField = this.createField(field.fields.left);
      if (leftField) leftWrapper.appendChild(leftField);
      wrapper.appendChild(leftWrapper);
    }

    if (field.fields?.right) {
      const rightWrapper = document.createElement("div");
      rightWrapper.className = "input-side right";
      const rightField = this.createField(field.fields.right);
      if (rightField) rightWrapper.appendChild(rightField);
      wrapper.appendChild(rightWrapper);
    }

    return wrapper;
  }

  private createSubmitButton(
    field: FormFieldConfig,
    wrapper: HTMLElement
  ): HTMLElement {
    const button = document.createElement("input");
    button.type = "submit";
    button.value = field.submitText || "Submit";

    const loadingText = document.createElement("span");
    loadingText.textContent = field.loadingText || "Submitting...";
    loadingText.style.display = "none";
    loadingText.className = "submit-loading";

    if (this.formClassConfig?.formFieldSubmit) {
      button.className = this.formClassConfig.formFieldSubmit;
    }

    button.appendChild(loadingText);
    wrapper.appendChild(button);

    return wrapper;
  }

  private async handleSubmit(e: Event) {
    console.log("formSender", this.formSender);
    console.log("handleSubmit");
    const form = e.target as HTMLFormElement;
    if (this.formSender?.submitUrl) {
      const url = this.formSender.submitUrl;
      const formData = new FormData(form);
      const data = Object.fromEntries(formData);
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
      if (response.ok) {
        this.hideStates(form);
        if (this.formSender?.isRedirect) {
          window.location.href = this.formSender.url;
        } else {
          this.showSucess(form);
        }
      } else {
        this.hideStates(form);
        this.showError(form, "An error occurred");
      }
    } else {
      console.error("No redirect URL found");
      this.showError(form, "No submit URL found");
    }
  }

  private showSucess(form: HTMLFormElement) {
    const parent = form.closest(`[${attributes.formParent}]`)?.parentElement;
    const successDiv = parent?.querySelector(
      `[${attributes.formSuccess}]`
    ) as HTMLElement;
    this.hideForm(form);
    if (successDiv) {
      successDiv.style.display = "block";
    } else {
      console.error("No success div found");
    }
  }

  private showError(form: HTMLFormElement, error?: string) {
    const parent = form.closest(`[${attributes.formParent}]`)?.parentElement;
    const errorDiv = parent?.querySelector(
      `[${attributes.formError}]`
    ) as HTMLElement;
    if (errorDiv) {
      errorDiv.style.display = "block";
      if (error) {
        errorDiv.textContent = error;
      }
    } else {
      console.error("No error div found");
    }
  }

  public hideForm(form: HTMLFormElement) {
    const parent = form.closest(`[${attributes.formParent}]`) as HTMLElement;
    if (parent) parent.style.display = "none";
  }

  public hideStates(form: HTMLFormElement) {
    //parent element
    const parent = form.closest(`[${attributes.formParent}]`)?.parentElement;

    const successDiv = parent?.querySelector(
      `[${attributes.formSuccess}]`
    ) as HTMLElement;
    const errorDiv = parent?.querySelector(
      `[${attributes.formError}]`
    ) as HTMLElement;
    if (successDiv) successDiv.style.display = "none";
    if (errorDiv) errorDiv.style.display = "none";
  }
}
